import { CommonModule } from "@angular/common";
import { Component, DestroyRef, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute } from "@angular/router";
import { map } from "rxjs";

import { CaseContextPageComponent } from "./case-context-page.component";

type IdentifierResolution =
  | { kind: "incarcerated-person"; incarceratedPersonId: number }
  | { kind: "scheduled-proceeding"; scheduledProceedingId: number; resolvedPersonId: number | null }
  | { kind: "unavailable"; reason: string };

/**
 * Route host for the inmate case-context / hearing-support flow.
 *
 * Accepts either an incarcerated-person identifier or a scheduled-proceeding
 * identifier from the route and resolves it into the inputs that
 * {@link CaseContextPageComponent} expects. Renders a clear unavailable state
 * when neither identifier resolves.
 *
 * Route shapes:
 *   /case-context/inmate/:incarceratedPersonId
 *   /case-context/proceeding/:scheduledProceedingId
 */
@Component({
  selector: "phms-case-context-route-host",
  standalone: true,
  imports: [CommonModule, CaseContextPageComponent],
  templateUrl: "./case-context-route-host.component.html",
  styleUrl: "./case-context-route-host.component.scss",
})
export class CaseContextRouteHostComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  resolution: IdentifierResolution = { kind: "unavailable", reason: "No identifier supplied." };

  constructor() {
    this.route.paramMap
      .pipe(
        map((params) => this.resolveIdentifier(params)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((resolution) => {
        this.resolution = resolution;
      });
  }

  get isIncarceratedPerson(): boolean {
    return this.resolution.kind === "incarcerated-person";
  }

  get isUnavailable(): boolean {
    return this.resolution.kind === "unavailable" || (this.resolution.kind === "scheduled-proceeding" && this.resolution.resolvedPersonId == null);
  }

  get incarceratedPersonId(): number | null {
    if (this.resolution.kind === "incarcerated-person") {
      return this.resolution.incarceratedPersonId;
    }
    if (this.resolution.kind === "scheduled-proceeding") {
      return this.resolution.resolvedPersonId;
    }
    return null;
  }

  get unavailableMessage(): string {
    if (this.resolution.kind === "unavailable") {
      return this.resolution.reason;
    }
    if (this.resolution.kind === "scheduled-proceeding" && this.resolution.resolvedPersonId == null) {
      return `Case context cannot be opened from scheduled proceeding ${this.resolution.scheduledProceedingId} because the incarcerated person could not be resolved yet.`;
    }
    return "Case context is unavailable.";
  }

  private resolveIdentifier(params: import("@angular/router").ParamMap): IdentifierResolution {
    const personIdRaw = params.get("incarceratedPersonId");
    const proceedingIdRaw = params.get("scheduledProceedingId");

    if (personIdRaw) {
      const id = Number(personIdRaw);
      if (!Number.isFinite(id) || id <= 0) {
        return {
          kind: "unavailable",
          reason: `'${personIdRaw}' is not a valid incarcerated person identifier.`,
        };
      }
      return { kind: "incarcerated-person", incarceratedPersonId: id };
    }

    if (proceedingIdRaw) {
      const id = Number(proceedingIdRaw);
      if (!Number.isFinite(id) || id <= 0) {
        return {
          kind: "unavailable",
          reason: `'${proceedingIdRaw}' is not a valid scheduled proceeding identifier.`,
        };
      }
      // Resolving a scheduled-proceeding identifier into an incarcerated-person
      // identifier requires a backend lookup that is not yet exposed; surface
      // the unavailable state instead of stubbing a local mapping.
      return { kind: "scheduled-proceeding", scheduledProceedingId: id, resolvedPersonId: null };
    }

    return { kind: "unavailable", reason: "No identifier supplied." };
  }
}
